/**
 * ProtectedRoute — wraps every page that requires authentication.
 *
 * If we're still checking `/api/auth/me` → render a thin loader.
 * If user is null → render <Login/>.
 * Otherwise → render the child route.
 *
 * Page access is decided by the access matrix (`surface` / `surfaces` props).
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";

export default function ProtectedRoute({ children, surface, surfaces }) {
  const { user, loading, can } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-500">
          <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  // The access matrix (Settings → Users → Permissions) is the only gate.
  // `surface` = one surface; `surfaces` = any-of (e.g. Settings sub-tabs).
  const needed = surfaces || (surface ? [surface] : []);
  if (needed.length && !needed.some(s => can(s, "read"))) {
    return (
      <div className="p-8 max-w-md mx-auto text-center" data-testid="forbidden">
        <div className="text-sm font-semibold text-slate-800 mb-1">Not allowed</div>
        <p className="text-sm text-slate-500">
          Your role ({user.role}) doesn't include access to this page. Ask an admin to grant it in
          Settings → Users → Permissions.
        </p>
      </div>
    );
  }

  return children;
}
